import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GoogleLogo, Envelope } from 'phosphor-react';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@southdevs/capacitor-google-auth';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { getAuthCallbackUrl } from '@/lib/authRedirect';

export default function Login() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = (loc.state as { returnTo?: string } | null)?.returnTo;

  useEffect(() => {
    if (user) nav(returnTo ?? '/decouvrir', { replace: true });
  }, [user, nav, returnTo]);
  const callbackUrl = getAuthCallbackUrl() + (returnTo ? `?next=${encodeURIComponent(returnTo)}` : '');

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<null | 'google'>(null);
  const [err, setErr] = useState<string | null>(null);

  async function magic(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setErr(null);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callbackUrl },
    });
    setLoading(false);
    if (error) setErr(error.message); else setSent(true);
  }

  async function oauth() {
    setErr(null);
    setOauthLoading('google');
    try {
      if (Capacitor.isNativePlatform()) {
        await GoogleAuth.initialize();
        const googleUser = await GoogleAuth.signIn({ scopes: ['email', 'profile'] }) as { idToken?: string; authentication?: { idToken?: string } };
        const idToken = googleUser.idToken ?? googleUser.authentication?.idToken ?? '';
        const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: callbackUrl },
        });
        if (error) throw error;
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Erreur de connexion Google');
      setOauthLoading(null);
    }
  }

  return (
    <div className="min-h-screen flex flex-col px-6 pt-[env(safe-area-inset-top)]">
      <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
        <BrandLogo size={72} className="text-terre mb-6" />
        <h1 className="t-ed-3xl mb-3">Bonjour.<br/>Entrez dans <em className="italic-keyword">le carnet</em>.</h1>
        <p className="t-ui text-ink-2 mb-10">Une fenêtre sur ce qui se passe près de chez vous.</p>

        <div className="space-y-3">
          <Button variant="secondary" full loading={oauthLoading === 'google'} iconLeft={<GoogleLogo size={18} />} onClick={oauth}>Continuer avec Google</Button>
        </div>

        <div className="my-7 flex items-center gap-3"><GoldenRule /><span className="t-cap text-ink-3">ou</span><GoldenRule /></div>

        {sent ? (
          <div className="text-center">
            <p className="t-ed-md italic text-ink-2">Vérifiez votre boîte mail.</p>
            <p className="t-ui-sm text-ink-3 mt-2">Lien magique envoyé à {email}.</p>
          </div>
        ) : (
          <form onSubmit={magic} className="space-y-3">
            <Input type="email" required placeholder="exemple@email.com" value={email} onChange={e => setEmail(e.target.value)} label="Email" />
            {err ? <p className="t-ui-sm text-danger">{err}</p> : null}
            <Button type="submit" variant="primary" full iconLeft={<Envelope size={18} />} loading={loading}>Recevoir un lien magique</Button>
          </form>
        )}
      </div>
      <p className="t-ui-xs text-ink-3 text-center pb-[max(env(safe-area-inset-bottom),1.5rem)]">En continuant, vous acceptez nos <Link to="/mentions#cgu" className="underline">CGU</Link> et notre <Link to="/mentions#confidentialite" className="underline">politique de confidentialité</Link>.</p>
    </div>
  );
}
